import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User, ShoppingCart, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import BrandMark from "@/components/BrandMark";
import { useInbox } from "@/hooks/useInbox";
import { useLang } from "@/i18n";
import IntlCurrencySelector from "@/components/intl/IntlCurrencySelector";

// Note: /98 is not in Tailwind's default opacity scale — bracket syntax is required.
const SCROLLED_CLASSES = "bg-cream/[0.98] backdrop-blur-xl border-b hairline shadow-sm".split(" ");

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { session } = useAuth();
  const location = useLocation();
  const { count: cartCount, setCartOpen } = useCart();
  const { unread: inboxUnread } = useInbox();
  const { market, t, route } = useLang();
  const headerRef = useRef<HTMLElement>(null);
  const isScrolledRef = useRef(false);

  const handleScroll = useCallback(() => {
    const scrolled = window.scrollY > 30;
    if (scrolled === isScrolledRef.current) return;
    isScrolledRef.current = scrolled;
    const header = headerRef.current;
    if (!header) return;
    if (scrolled) {
      header.classList.add(...SCROLLED_CLASSES);
      header.classList.remove("bg-transparent");
    } else {
      header.classList.remove(...SCROLLED_CLASSES);
      header.classList.add("bg-transparent");
    }
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const navItems = market === "intl"
    ? [
        { name: t("nav.map"), path: route("map"), external: false },
        { name: t("nav.archetype"), path: route("archetype"), external: false },
        { name: t("nav.letters"), path: route("letters"), external: false },
        { name: t("nav.about"), path: route("about"), external: false },
      ]
    : [
        { name: "Knygynas", path: "/knygynas", external: false },
        { name: "Pamokos", path: "/pamokos", external: false },
        { name: "Žemėlapis", path: "/zemelapis", external: false },
        { name: "Archetipas", path: "/archetipas", external: false },
        { name: "Apie", path: "/apie", external: false },
      ];

  const profileLink = session ? route("profile") : route("auth");
  const inboxLink = market === "intl" ? route("profile") : route("inbox");

  return (
    <header
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-transparent"
    >
      <nav className="container mx-auto px-6 py-5">
        <div className="flex items-center justify-between gap-6">
          <Link to="/" className="transition-opacity hover:opacity-70">
            <BrandMark variant="dark" />
          </Link>

          {/* Desktop nav links */}
          <ul className="hidden lg:flex items-center gap-8 ml-auto">
            {navItems.map((item) => (
              <li key={item.path}>
                {item.external ? (
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium tracking-wide text-ink/80 hover:text-ink transition-colors"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    to={item.path}
                    className={cn(
                      "text-sm font-medium tracking-wide transition-colors",
                      location.pathname === item.path
                        ? "text-ink"
                        : "text-ink/70 hover:text-ink"
                    )}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Right side actions (desktop) */}
          <div className="hidden lg:flex items-center gap-2">
            {market === "intl" && <IntlCurrencySelector compact />}
            {session && (
              <Link
                to={inboxLink}
                className="relative p-2.5 rounded-full text-ink/70 hover:text-ink hover:bg-ink/5 transition-colors"
                aria-label={inboxUnread > 0 ? t("nav.unreadLetters", { count: inboxUnread }) : t("nav.letters")}
              >
                <Mail size={18} />
                {inboxUnread > 0 && (
                  <span className="absolute top-0 right-0 min-w-4 h-4 px-1 bg-terracotta text-cream text-[9px] font-bold rounded-full flex items-center justify-center">
                    {inboxUnread}
                  </span>
                )}
              </Link>
            )}
            {market === "lt" && (
              <button
                onClick={() => setCartOpen(true)}
                className="relative p-2.5 rounded-full text-ink/70 hover:text-ink hover:bg-ink/5 transition-colors"
                aria-label={t("nav.cart")}
              >
                <ShoppingCart size={18} />
                {cartCount > 0 && (
                  <span className="absolute top-0 right-0 w-4 h-4 bg-terracotta text-cream text-[9px] font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
            )}
            <Button
              asChild
              size="sm"
              className="rounded-full h-10 px-5 bg-ink text-cream hover:bg-ink/85 font-semibold"
            >
              <Link to={profileLink} className="relative">
                <User size={15} />
                {t("nav.profile")}
              </Link>
            </Button>
          </div>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center gap-1">
            {market === "intl" && <IntlCurrencySelector compact />}
            {session && (
              <Link
                to={inboxLink}
                className="relative p-2 rounded-full text-ink/80 hover:bg-ink/5"
                aria-label={inboxUnread > 0 ? t("nav.unreadLetters", { count: inboxUnread }) : t("nav.letters")}
              >
                <Mail size={20} />
                {inboxUnread > 0 && (
                  <span className="absolute top-0 right-0 min-w-4 h-4 px-1 bg-terracotta text-cream text-[9px] font-bold rounded-full flex items-center justify-center">
                    {inboxUnread}
                  </span>
                )}
              </Link>
            )}
            {market === "lt" && (
              <button
                onClick={() => setCartOpen(true)}
                className="relative p-2 rounded-full text-ink/80 hover:bg-ink/5"
                aria-label={t("nav.cart")}
              >
                <ShoppingCart size={20} />
                {cartCount > 0 && (
                  <span className="absolute top-0 right-0 w-4 h-4 bg-terracotta text-cream text-[9px] font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-ink"
              aria-label={t("nav.toggleMenu")}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        <div
          className={cn(
            "lg:hidden overflow-hidden transition-all duration-300",
            isOpen ? "max-h-[500px] opacity-100 mt-4" : "max-h-0 opacity-0"
          )}
        >
          <ul className="flex flex-col gap-1 py-4 bg-cream-soft rounded-2xl px-3 border hairline">
            {navItems.map((item) => (
              <li key={item.path}>
                {item.external ? (
                  <a
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsOpen(false)}
                    className="block px-4 py-3 rounded-xl text-ink font-medium hover:bg-ink/5"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "block px-4 py-3 rounded-xl font-medium transition-colors",
                      location.pathname === item.path
                        ? "bg-ink/10 text-ink"
                        : "text-ink/85 hover:bg-ink/5"
                    )}
                  >
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
            <li className="pt-2 mt-2 border-t hairline">
              <Button
                asChild
                className="w-full rounded-full bg-ink text-cream hover:bg-ink/85"
                onClick={() => setIsOpen(false)}
              >
                <Link to={profileLink}>
                  <User size={16} className="mr-2" />
                  {t("nav.profile")}
                  {session && inboxUnread > 0 && (
                    <span className="ml-2 min-w-[18px] h-[18px] px-1 bg-terracotta text-cream text-[10px] font-bold rounded-full inline-flex items-center justify-center">
                      {inboxUnread}
                    </span>
                  )}
                </Link>
              </Button>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
